// WP-1.18 (SR-4, S-10): free-text note queries/mutations for any major entity.
import { noteListResponseSchema, noteSchema, type Note } from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';

export const NOTES_KEY = ['notes'] as const;

export function useNotes(entityType: string, entityId: string): UseQueryResult<Note[]> {
  return useQuery({
    queryKey: [...NOTES_KEY, entityType, entityId],
    queryFn: async () => {
      const params = new URLSearchParams({ entityType, entityId });
      const body = noteListResponseSchema.parse(
        await api.get(`/api/v1/notes?${params.toString()}`),
      );
      return body.notes;
    },
  });
}

function useInvalidateNotes(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: NOTES_KEY });
  };
}

export function useCreateNote(): UseMutationResult<
  Note,
  Error,
  { entityType: string; entityId: string; body: string }
> {
  const invalidate = useInvalidateNotes();
  return useMutation({
    mutationFn: async (input) => noteSchema.parse(await api.post('/api/v1/notes', input)),
    onSuccess: invalidate,
  });
}

export function useUpdateNote(): UseMutationResult<Note, Error, { id: string; body: string }> {
  const invalidate = useInvalidateNotes();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      noteSchema.parse(await api.patch(`/api/v1/notes/${id}`, { body })),
    onSuccess: invalidate,
  });
}

export function useDeleteNote(): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateNotes();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/notes/${id}`);
    },
    onSuccess: invalidate,
  });
}
